import { OpenInNew } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { changeInvitationIsValidated } from '@/domain/external-invitation/actions/change-invitation-isValidated.action';
import {
  ExternalInvitation,
  Status,
} from '@/domain/external-invitation/entities/external-invitation.entity';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { useAppDispatch } from '@/ui/hook/store';
import { language, translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { buildExternalLink } from '../utils/build-external-link';
import { getButtonStatusAndValidateData } from './ExternalInvitationsList.utils';

export function ExternalInvitationsList({
  invitations,
}: {
  invitations: ExternalInvitation[];
}) {
  const dispatch = useAppDispatch();

  const onChangeIsValidated = (
    invitation: ExternalInvitation,
    isValidated: boolean,
  ) => {
    dispatch(changeInvitationIsValidated({ invitation, isValidated }));
  };

  invitations.sort((a, b) => {
    return a.id - b.id;
  });

  return (
    <Box>
      <Box sx={{ my: 2 }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: '900' }}>
          {translate(
            `InviteExternalToFill.invitations-list.assigned-to-X-persons`,
            { count: invitations.length },
          )}
        </Typography>
      </Box>
      {invitations.length > 0 && (
        <TableContainer>
          <Table>
            <TableHeaderCells />
            <TableBodyCells
              externalInvitations={invitations}
              onChangeIsValidated={onChangeIsValidated}
            />
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function TableHeaderCells() {
  return (
    <TableHead>
      <TableRow>
        <TableCell />
        <TableCell sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
          {translate('InviteExternalToFill.create-and-send-invitation.name')}
        </TableCell>
        <TableCell sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
          {translate(
            'InviteExternalToFill.create-and-send-invitation.limit-date',
          )}
        </TableCell>
        <TableCell sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
          {translate('InviteExternalToFill.invitations-list.link')}
        </TableCell>
        <TableCell sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
          {translate(
            'InviteExternalToFill.create-and-send-invitation.category',
          )}
        </TableCell>
        <TableCell sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
          {translate('InviteExternalToFill.invitations-list.progress')}
        </TableCell>
        <TableCell sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
          {translate('InviteExternalToFill.invitations-list.co2-impact')}
        </TableCell>
        <TableCell sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
          {translate('InviteExternalToFill.invitations-list.status')}
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

function TableBodyCells({
  externalInvitations,
  onChangeIsValidated,
}: {
  externalInvitations: ExternalInvitation[];
  onChangeIsValidated: (
    invitation: ExternalInvitation,
    isValidated: boolean,
  ) => void;
}) {
  return (
    <TableBody>
      {externalInvitations.map((x) => (
        <TableRow
          key={x.id}
          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
        >
          <TableCell padding="checkbox">
            <Avatar
              sx={{
                bgcolor: ColorsRaw.main.Navy_Dark,
                color: ColorsRaw.basic.Cream,
              }}
            >
              {getFirstLetter(x.name)}
            </Avatar>
          </TableCell>
          <TableCell>
            <Stack>
              <Typography>{x.name}</Typography>
              <Typography sx={{ fontSize: '0.75rem' }}>{x.email}</Typography>
            </Stack>
          </TableCell>
          <TableCell>
            <Typography>{formatDate(x.expirationDate)}</Typography>
          </TableCell>
          <TableCell>
            <IconButton onClick={() => window.open(buildExternalLink(x))}>
              <Tooltip title={buildExternalLink(x)}>
                <OpenInNew sx={{ color: ColorsRaw.basic.Cream }} />
              </Tooltip>
            </IconButton>
          </TableCell>
          <TableCell>
            <Typography sx={{ fontWeight: 'light' }}>
              {formatCategory(x)}
            </Typography>
          </TableCell>
          <TableCell>
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <Typography>{Math.floor(x.completion * 100)}%</Typography>
              <LinearProgress
                value={Math.floor(x.completion * 100)}
                variant="determinate"
                sx={{
                  borderRadius: '0.25rem',
                  width: '3rem',
                }}
              />
            </Stack>
          </TableCell>
          <TableCell>
            <Chip
              sx={{ fontWeight: '700' }}
              label={translate(
                'InviteExternalToFill.invitations-list.co2-impact-value',
                { value: x.co2ImpactScore },
              )}
            />
          </TableCell>
          <TableCell>
            <CustomButtonStatusAndValidate
              status={x.status}
              isValidated={x.isValidated}
              onChangeIsValidated={(value) => onChangeIsValidated(x, value)}
            />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
}

function getFirstLetter(text: string) {
  try {
    return text.slice(0, 1);
  } catch {
    return '';
  }
}

function formatDate(date: string) {
  const dateObject = new Date(date);
  if (!isValidDate(dateObject)) {
    return '-';
  }
  return dateObject.toLocaleDateString(language);
}

function formatCategory(invitation: ExternalInvitation) {
  let category = invitation.category;
  if (category === '') {
    category = 'all';
  }
  return translate(`InviteExternalToFill.category.${category}`);
}

const isValidDate = (date: Date) => {
  return date instanceof Date && !isNaN(date.getTime());
};

function CustomButtonStatusAndValidate({
  status,
  isValidated,
  onChangeIsValidated,
}: {
  status: Status;
  isValidated: boolean;
  onChangeIsValidated: (value: boolean) => void;
}) {
  const [isHover, setIsHover] = useState(false);

  const { text, textColor, bgColor, icon } = getButtonStatusAndValidateData(
    status,
    isValidated,
    isHover,
  );

  const onClick = () => {
    onChangeIsValidated(!isValidated);
  };

  return (
    <FSPrimaryButton
      startIcon={icon}
      onClick={onClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      sx={{
        color: textColor,
        backgroundColor: bgColor,
        '&:hover': { backgroundColor: bgColor },
      }}
    >
      <Typography sx={{ width: '4rem', fontWeight: 700 }}>{text}</Typography>
    </FSPrimaryButton>
  );
}
